import React, {useState} from 'react';
import DetailsCard from '../DetailsCard';
import {createUseStyles} from 'react-jss';
import {Colors} from '../../constants/colors';
import {CampaignResult} from '../../redux/campaignTypes';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheckCircle, faEye, faTimesCircle, faTriangleExclamation} from '@fortawesome/free-solid-svg-icons';
import CampaignRecipientsModal from "./CampaignRecipientsModal";

interface Props {
  result: CampaignResult;
}

const CampaignResultCard: React.FC<Props> = ({result}) => {
  const styles = useStyles();
  const [showSuccessRecipientsModal, setShowSuccessRecipientsModal] = useState(false)
  const [showFailedRecipientsModal, setShowFailedRecipientsModal] = useState(false)

  const successfulCount = result.successfulRecipients?.length ?? 0;
  const failedCount = result.failedRecipients?.length ?? 0;

  const CountBadge: React.FC<{ count: number; onClick?: () => void }> = ({count, onClick}) => (
    <div className={styles.countBadge} onClick={(e) => {
      e.stopPropagation();
      onClick?.();
    }}>
      <span className={styles.countNumber}>{count.toLocaleString()}</span>
      <FontAwesomeIcon icon={faEye} className={styles.viewIcon}/>
    </div>
  );

  return (
    <>
      <DetailsCard>
        <h6>Campaign result</h6>

        {result.errorMessage && (
          <div className={styles.errorBox}>
            <FontAwesomeIcon icon={faTriangleExclamation} className={styles.errorIcon}/>
            <span>{result.errorMessage}</span>
          </div>
        )}

        <div className={styles.row}>
          <div className={styles.left}>
            <FontAwesomeIcon icon={faCheckCircle} className={styles.successIcon}/>
            <span>Successful recipients</span>
          </div>
          <CountBadge count={successfulCount} onClick={() => setShowSuccessRecipientsModal(true)}/>
        </div>

        <div className={styles.row}>
          <div className={styles.left}>
            <FontAwesomeIcon icon={faTimesCircle} className={styles.failedIcon}/>
            <span>Failed recipients</span>
          </div>
          <CountBadge count={failedCount} onClick={() => setShowFailedRecipientsModal(true)}/>
        </div>
      </DetailsCard>

      <CampaignRecipientsModal
        recipients={result.successfulRecipients}
        notice={"This list shows the recipients that were successfully sent the campaign. It does not indicate whether the recipients received the campaign (this depends on, for example, notification preferences)."}
        visible={showSuccessRecipientsModal}
        onCloseClick={() => setShowSuccessRecipientsModal(false)}
      />
      <CampaignRecipientsModal
        recipients={result.failedRecipients}
        visible={showFailedRecipientsModal}
        onCloseClick={() => setShowFailedRecipientsModal(false)}
      />

    </>
  );
};

const useStyles = createUseStyles({
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 8,
    gap: 10,
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: Colors.white,
  },
  successIcon: {
    color: '#37b24d',
    fontSize: 14,
  },
  failedIcon: {
    color: '#f03e3e',
    fontSize: 14,
  },
  countBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 12px',
    backgroundColor: Colors.darkGrayCard,
    border: `1px solid ${Colors.darkGrayBorder}`,
    borderRadius: 16,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
      borderColor: Colors.white,
    },
  },
  countNumber: {
    color: Colors.white,
    fontWeight: 'bold',
    fontSize: 13,
  },
  viewIcon: {
    color: Colors.darkGray1,
    fontSize: 12,
    transition: 'color 0.2s ease',
    '$countBadge:hover &': {
      color: Colors.white,
    },
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    backgroundColor: Colors.darkGrayCard,
    border: `1px solid ${Colors.darkGrayBorder}`,
    borderRadius: 6,
    color: Colors.darkGray1,
    marginBottom: 8,
  },
  errorIcon: {
    color: '#fcc419',
  },
});

export default CampaignResultCard;
